/**
 * Theme: product page variant state.
 *
 * WHAT THIS DOES, and deliberately no more:
 *   - tracks the selected colour and size
 *   - swaps which gallery tiles are visible when the colour changes
 *   - resolves the variant, writes it into Horizon's `variantId` input and
 *     into the URL
 *   - repaints price, the stock line and the Add to bag state
 *
 * WHAT IT DOES NOT DO: adding to cart. That is Horizon's
 * <product-form-component>, reused verbatim. We only keep its hidden input
 * honest, exactly as the header reuses <cart-icon> and only paints the badge.
 *
 * GALLERY: SCROLL, DO NOT FILTER. All tiles stay visible at all times.
 * Choosing a colour scrolls the gallery's own scroller to that colour's
 * featured image.
 *
 * The earlier model hid tiles whose variant tag did not match. That was wrong:
 * Shopify allows only ONE featured image per variant, so tag-filtering assumed
 * data that cannot exist. A colour with four photos can only ever tag one of
 * them, so filtering could never show the other three.
 *
 * The scroll happens inside .core-pdp__scroller, which owns its own overflow.
 * The page must not move.
 */
(function () {
  'use strict';

  function init(root) {
    var json = root.querySelector('[data-core-variants]');
    if (!json) return;

    var variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (error) {
      // Malformed variant data must not take the page down. The form still
      // works with whatever variant Liquid rendered.
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    var colourPos = Number(root.dataset.colourPos || 0);
    var sizePos = Number(root.dataset.sizePos || 0);

    var swatches = Array.prototype.slice.call(root.querySelectorAll('[data-colour-value]'));
    var sizes = Array.prototype.slice.call(root.querySelectorAll('[data-size-value]'));
    var scroller = root.querySelector('.core-pdp__scroller');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // The one place this file knows the gallery has two layouts. See
    // scrollToColour: above this width nothing about its behaviour changes.
    var mobileGallery = window.matchMedia('(max-width: 750px)');

    var priceEl = root.querySelector('[data-price]');
    var colourNameEl = root.querySelector('[data-colour-name]');
    var stockEl = root.querySelector('[data-stock]');
    var stockSizeEl = root.querySelector('[data-stock-size]');
    var stockStateEl = root.querySelector('[data-stock-state]');
    var addButton = root.querySelector('[data-add-button]');
    var addLabel = root.querySelector('[data-add-label]');
    var variantInput = root.querySelector('input[name="id"]');

    /**
     * The price line, with a struck was-price when the variant is marked down.
     *
     * ⚠️ REBUILDS THE CHILDREN FROM SCRATCH EVERY TIME, and that is what keeps
     * the no-markdown case clean: switching from a discounted variant to a
     * full-price one leaves no empty <s>, no stray space and no hidden node,
     * because nothing from the previous state survives. The shape built here is
     * node-for-node the Liquid in sections/core-main-product.liquid, so the gap
     * a shopper lands on is the gap after a tap. A matching copy lives in
     * assets/core-pdp-sticky.js; the two files are separate classic scripts with
     * no shared module, so change both or neither.
     *
     * @param {Element} el  [data-price]
     * @param {string} nowText  the formatted price
     * @param {?string} wasText  the formatted compare-at, or null for none
     */
    function renderPrice(el, nowText, wasText) {
      if (!el || !nowText) return;
      el.textContent = '';
      if (wasText) {
        var was = document.createElement('s');
        was.className = 'core-pdp__price-was';
        was.textContent = wasText;
        el.appendChild(was);
        el.appendChild(document.createTextNode(' '));
      }
      var now = document.createElement('span');
      now.className = 'core-pdp__price-now';
      now.textContent = nowText;
      el.appendChild(now);
    }

    /**
     * The formatted compare-at for a variant, but ONLY when it is a real
     * markdown. A compare-at equal to or below the price is not a sale and
     * renders nothing, matching the Liquid's strict `>`.
     *
     * @returns {?string}
     */
    function wasPriceFor(v) {
      if (!v || v.compare_at_price == null) return null;
      return v.compare_at_price > v.price ? v.compare_at_price_formatted : null;
    }

    /** @returns {string} the option value at a 1-based position. */
    function optionAt(variant, pos) {
      return pos > 0 && variant.options ? variant.options[pos - 1] : '';
    }

    var state = {
      colour: swatches.length ? findChecked(swatches, 'colourValue') : '',
      size: '',
    };

    function findChecked(list, key) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].getAttribute('aria-checked') === 'true') return list[i].dataset[key];
      }
      return list.length ? list[0].dataset[key] : '';
    }

    function match() {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (colourPos && state.colour && optionAt(v, colourPos) !== state.colour) continue;
        if (sizePos && state.size && optionAt(v, sizePos) !== state.size) continue;
        if (sizePos && !state.size) continue;
        return v;
      }
      return null;
    }

    /** First variant of the selected colour, used before a size is picked. */
    function colourRepresentative() {
      for (var i = 0; i < variants.length; i++) {
        if (!colourPos || optionAt(variants[i], colourPos) === state.colour) return variants[i];
      }
      return variants[0];
    }

    /**
     * Scroll the gallery to the featured image of the chosen colour.
     *
     * Resolved from the colour's first AVAILABLE variant, falling back to its
     * first variant of any availability, because a colour whose XS is sold out
     * still has a photograph.
     *
     * If nothing resolves, or the tile is not in the DOM, DO NOTHING. Scrolling
     * to the top would be worse than staying put: it would move the gallery
     * away from whatever the shopper was looking at and imply the colour has no
     * images, which is not what "no featured image set" means.
     */
    function scrollToColour() {
      if (!colourPos || !state.colour) return;

      var chosen = null;
      for (var i = 0; i < variants.length; i++) {
        if (optionAt(variants[i], colourPos) !== state.colour) continue;
        if (variants[i].featured_media_id == null) continue;
        if (variants[i].available) { chosen = variants[i]; break; }
        if (!chosen) chosen = variants[i];
      }
      if (!chosen) return;

      // MOBILE: the gallery is a horizontal snap track, not a vertical
      // scroller, so hand the media id to it and let it move on its own axis.
      // An event rather than a direct call, so this file needs no reference to
      // assets/core-pdp-gallery.js and neither has to load first. If that script
      // is absent the event goes unheard, which is a no-op, not a break.
      if (mobileGallery.matches) {
        root.dispatchEvent(
          new CustomEvent('core:gallery:goto', {
            detail: { mediaId: chosen.featured_media_id },
          })
        );
        return;
      }

      // DESKTOP: unchanged. The vertical scroll inside .core-pdp__scroller,
      // exactly as before. The scroller guard moved down here from the top of
      // the function because the mobile path above does not use it.
      if (!scroller) return;

      var tile = root.querySelector('[data-media-id="' + chosen.featured_media_id + '"]');
      if (!tile) return;

      // Delta between the tile and the scroller, so this works regardless of
      // where the scroller sits on the page or how far it is already scrolled.
      var delta = tile.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      scroller.scrollTo({
        top: scroller.scrollTop + delta,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    /** Mark each size available or not FOR THE CHOSEN COLOUR. */
    function paintSizeAvailability() {
      sizes.forEach(function (button) {
        var value = button.dataset.sizeValue;
        var available = variants.some(function (v) {
          if (colourPos && state.colour && optionAt(v, colourPos) !== state.colour) return false;
          return optionAt(v, sizePos) === value && v.available;
        });
        button.setAttribute('data-available', available ? 'true' : 'false');
      });
    }

    // ---- add to bag: state helpers, shared by paint() and the click guard ----

    /** @returns {boolean} is any size of the chosen colour still in stock? */
    function colourHasStock() {
      return variants.some(function (v) {
        if (colourPos && state.colour && optionAt(v, colourPos) !== state.colour) return false;
        return v.available;
      });
    }

    /** @returns {boolean} can the CURRENT selection actually be added? */
    function canAddNow() {
      var v = match();
      return Boolean(v && v.available);
    }

    /** Awaiting a size is not the same as unbuyable, so it is not disabled. */
    function awaitingSize() {
      return Boolean(sizePos) && !state.size;
    }

    function isBuyable() {
      return awaitingSize() ? colourHasStock() : canAddNow();
    }

    function currentLabel() {
      return isBuyable() ? 'Add to bag' : 'Sold Out';
    }

    var nudgeTimer = null;

    /**
     * The concept's feedback: the button says what is missing, on itself, for
     * 1400ms. No alert, no scroll, nothing added.
     */
    function nudgeSelectSize() {
      if (!addLabel) return;
      clearTimeout(nudgeTimer);
      addLabel.textContent = 'Select a size first';
      nudgeTimer = setTimeout(function () {
        nudgeTimer = null;
        addLabel.textContent = currentLabel();
      }, 1400);
    }

    function paint() {
      swatches.forEach(function (b) {
        b.setAttribute('aria-checked', b.dataset.colourValue === state.colour ? 'true' : 'false');
      });
      sizes.forEach(function (b) {
        b.setAttribute('aria-checked', b.dataset.sizeValue === state.size ? 'true' : 'false');
      });

      if (colourNameEl) colourNameEl.textContent = state.colour;
      paintSizeAvailability();

      var variant = match();
      var display = variant || colourRepresentative();

      // Price and was-price come from the SAME object, `display`, so the struck
      // figure can never belong to a different variant from the one beside it.
      // Before a size is chosen `display` is the colour's representative, which
      // is also what the price has always shown in that state.
      if (priceEl && display) renderPrice(priceEl, display.price_formatted, wasPriceFor(display));

      // The stock line appears only once a size is picked.
      if (stockEl) {
        if (state.size && variant) {
          // Two spans: the concept colours only the state word, leaving the
          // size code in ink. Writing one string would tint both.
          stockEl.hidden = false;
          if (stockSizeEl) stockSizeEl.textContent = state.size;
          if (stockStateEl) stockStateEl.textContent = variant.available ? 'In Stock' : 'Sold Out';
          stockEl.setAttribute('data-state', variant.available ? 'in' : 'out');
        } else {
          stockEl.hidden = true;
          stockEl.removeAttribute('data-state');
        }
      }

      // THE BUTTON STAYS LIVE UNTIL THERE IS NOTHING TO BUY.
      //
      // It used to disable whenever no variant resolved, and with a size option
      // that is true on load, so the page opened with a greyed-out button. The
      // concept keeps it solid and answers a premature click with a nudge, which
      // tells the shopper what to do instead of silently refusing.
      //
      // Disabled is reserved for genuinely unbuyable: every size in the chosen
      // colour gone, or a chosen size that is sold out. Those keep the "Sold Out"
      // label, so the dead state and the nudge never look alike.
      if (addButton) {
        addButton.disabled = !isBuyable();
        if (addLabel && !nudgeTimer) addLabel.textContent = currentLabel();
      }

      if (variantInput && variant) variantInput.value = String(variant.id);

      // Keep the URL shareable, without adding a history entry per tap.
      if (variant && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      }

      // ⚠️ THE ONE PUBLIC SIGNAL FOR VARIANT STATE. Added 13 August 2026 for the
      // mobile sticky bar, and it is the reason that bar can be a remote control
      // rather than a second copy of this logic.
      //
      // Everything above is this file's private business. Anything else that
      // needs to know what is selected subscribes to this instead of re-deriving
      // it from the DOM, so there is exactly one place that decides what
      // "awaiting a size" or "buyable" means. Emitted at the END of paint(), so
      // a listener reading the DOM in the handler sees the finished state.
      //
      // Same shape of seam as ats:gallery:goto above: an event, not a shared
      // object, so neither file has to load first and nothing breaks if the
      // listener is absent.
      root.dispatchEvent(
        new CustomEvent('core:variant:change', {
          bubbles: true,
          detail: {
            colour: state.colour || null,
            size: state.size || null,
            awaitingSize: awaitingSize(),
            buyable: isBuyable(),
            variantId: variant ? variant.id : null,
            available: variant ? variant.available : null,
            priceFormatted: display ? display.price_formatted : null,
            // Null unless `display` is genuinely marked down, so a listener
            // can gate on truthiness and never render an empty struck price.
            priceWasFormatted: wasPriceFor(display),
            mediaId: display ? display.featured_media_id : null,
            label: currentLabel(),
          },
        })
      );
    }

    // THE CLICK GUARD, and how it coexists with Horizon's real add to cart.
    //
    // The button is type="submit" inside {% form 'product' %}, and
    // <product-form-component> listens for the form's SUBMIT event. Preventing
    // the default on this CLICK stops the submit event from ever firing, so
    // Horizon is never reached and never has to know about the nudge. A valid
    // click is not touched at all: it falls straight through to the real
    // fetch-based add, the cart drawer and the header bubble, exactly as before.
    //
    // No stopPropagation: it is not needed to block the submit and would
    // interfere with the delegated listeners further down.
    if (addButton) {
      addButton.addEventListener('click', function (event) {
        if (awaitingSize()) {
          event.preventDefault();
          nudgeSelectSize();
          return;
        }
        // A chosen-but-sold-out size. The button already reads Sold Out and is
        // disabled, so this is belt and braces rather than the usual path.
        if (!canAddNow()) event.preventDefault();
      });
    }

    /* ⚠️ RE-ANNOUNCE ON REQUEST, AND IT IS NOT OPTIONAL POLISH.
       This file runs at its own script's execution, which with `defer` is
       BEFORE DOMContentLoaded, so its first ats:variant:change is dispatched
       before any later-loading listener exists. The sticky bar therefore opened
       with an empty awareness line and no idea what was selected.

       Fixed by letting a listener ASK rather than by reordering the scripts,
       which would only work until someone moved a tag: whoever wants the
       current state dispatches ats:variant:request on the root and gets a fresh
       ats:variant:change back. paint() is idempotent, so asking twice is free,
       and this file stays the only thing that decides what the state IS. */
    root.addEventListener('core:variant:request', function () {
      paint();
    });

    swatches.forEach(function (button) {
      button.addEventListener('click', function () {
        state.colour = button.dataset.colourValue;
        paint();
        // Only on an actual choice, never on the initial paint, so the page
        // does not jump the gallery on load.
        scrollToColour();
      });
    });

    sizes.forEach(function (button) {
      button.addEventListener('click', function () {
        state.size = button.dataset.sizeValue;
        paint();
      });
    });

    // ---- image lightbox ----------------------------------------------------
    // Concept behaviour, reproduced: clicking a tile opens a full-screen white
    // viewer at that image; the arrows WRAP, because the concept's step() is
    //   current = (current + d + n) % n
    // which is modular, not clamped. Escape, ArrowLeft and ArrowRight work while
    // it is open, and a click on the backdrop closes it.
    //
    // The shell is a <dialog>, so focus trapping, page inertness, Escape and
    // focus RETURN to the tile all come from the platform rather than from us.
    (function lightbox() {
      var lb = document.getElementById('core-lightbox');
      if (!lb) return;
      var image = lb.querySelector('[data-lb-image]');
      var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-lb-index]'));
      if (!image || !tiles.length) return;

      var current = 0;

      function paint() {
        var tile = tiles[current];
        if (!tile) return;

        // Size the element to THIS image before the source lands. The tiles and
        // cards crop to 3:4; the zoom is where the cropped-away detail has to be
        // visible, so it must never inherit a 3:4 box. Without this the width
        // and height attributes would still describe the previous image, and on
        // first open the markup's own values, boxing a square photograph into
        // the very shape this dialog exists to escape.
        if (tile.dataset.lbW && tile.dataset.lbH) {
          image.setAttribute('width', tile.dataset.lbW);
          image.setAttribute('height', tile.dataset.lbH);
        }

        image.src = tile.dataset.lbFull || '';
        image.alt = tile.dataset.lbAlt || '';
      }

      function open(index) {
        current = index;
        paint();
        // showModal, not the concept's class toggle: this is what makes the rest
        // of the page inert and hands focus back to the tile on close.
        if (typeof lb.showModal === 'function' && !lb.open) lb.showModal();
      }

      /** Wrap, matching the concept's modular step. */
      function step(delta) {
        current = (current + delta + tiles.length) % tiles.length;
        paint();
      }

      tiles.forEach(function (tile) {
        tile.addEventListener('click', function () {
          open(Number(tile.dataset.lbIndex) || 0);
        });
      });

      lb.querySelectorAll('[data-lb-step]').forEach(function (button) {
        button.addEventListener('click', function () {
          step(Number(button.dataset.lbStep));
        });
      });

      var closeButton = lb.querySelector('[data-lb-close]');
      if (closeButton) {
        closeButton.addEventListener('click', function () {
          lb.close();
        });
      }

      // Backdrop click. With a full-bleed dialog the "backdrop" is the dialog's
      // own padding area, so the concept's e.target === lb test still holds.
      lb.addEventListener('click', function (event) {
        if (event.target === lb) lb.close();
      });

      // Escape is native to <dialog>; the arrows are not.
      lb.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        }
      });
    })();

    // ---- description clamp ------------------------------------------------
    // Measure and TRIM, do not -webkit-line-clamp with a fade: that left
    // half-visible letters behind the toggle and read as a rendering fault.
    // Binary-search the longest string that still fits two lines, cut at a
    // word boundary, append an ellipsis, and put the toggle straight after.
    (function clamp() {
      // Measure the BLOCK <p>, mutate the <span> inside it, and let the button
      // flow inline after the ellipsis. Measuring the mutated element directly
      // does not work: it has to stay inline to sit beside the button, and an
      // inline element reports scrollHeight 0, so the fit test always passed and
      // the toggle never appeared.
      var box = root.querySelector('[data-desc-text]');
      var body = root.querySelector('[data-desc-body]');
      var toggle = root.querySelector('[data-desc-toggle]');
      if (!box || !body || !toggle) return;

      /* ⚠️ DROP THE BORN-CLAMPED CLASS BEFORE MEASURING ANYTHING. The markup
         ships with .core-pdp__desc-text--clamp so the description is never
         painted open on a cold load; from here the exact trim below takes
         over, and the CSS clamp would otherwise cap scrollHeight and make
         every fit test pass.

         No frame can be painted between this line and the trim: both happen
         inside one task, and the browser does not paint mid-task. That is why
         removing it here does not reintroduce the flash it was added to
         prevent. */
      box.classList.remove('core-pdp__desc-text--clamp');

      var full = body.textContent.replace(/\s+/g, ' ').trim();
      if (!full) return;

      var LINES = 2;
      var expanded = false;

      /** @returns {boolean} does the current content fit two lines? */
      function fits() {
        var lh = parseFloat(getComputedStyle(box).lineHeight);
        if (isNaN(lh)) lh = 21;
        return box.scrollHeight <= lh * LINES + 1;
      }

      function render() {
        if (expanded) {
          body.textContent = full + ' ';
          toggle.removeAttribute('hidden');
          toggle.textContent = 'Show less';
          return;
        }

        toggle.textContent = 'Show more';
        body.textContent = full;

        // The toggle must be laid out while we measure, or the last line has
        // room the real layout will not have, and the clamp overshoots.
        toggle.removeAttribute('hidden');

        if (fits()) {
          // Short enough already: no toggle, no ellipsis. The concept does the
          // same, so a one-line description never shows a dead control.
          toggle.setAttribute('hidden', '');
          return;
        }

        // Binary-search the longest prefix that still fits, cutting at a word
        // boundary inside the loop so the measured string is the rendered one.
        var lo = 0;
        var hi = full.length;
        var best = 0;
        while (lo <= hi) {
          var mid = (lo + hi) >> 1;
          body.textContent = full.slice(0, mid).replace(/\s+\S*$/, '') + '\u2026';
          if (fits()) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
        }
        body.textContent = full.slice(0, best).replace(/\s+\S*$/, '') + '\u2026';
      }

      toggle.addEventListener('click', function () {
        expanded = !expanded;
        render();
      });

      // Re-measure on resize: the available width decides where two lines end.
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(render, 120);
      });

      render();
    })();

    // ---- wishlist save ----------------------------------------------------
    // Keys on the resolved variant through the shared store, so the same
    // product saved in two colours does not collide, and the header badge
    // repaints from the same wishlist:change event everything else listens on.
    (function save() {
      var button = root.querySelector('[data-wishlist-toggle]');
      if (!button) return;

      function key() {
        var variant = match() || colourRepresentative();
        return variant ? [variant.id, state.colour || '', state.size || ''] : null;
      }

      function reflect() {
        var k = key();
        var saved = Boolean(k && window.WISH && window.WISH.has(k[0], k[1], k[2]));
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        button.setAttribute('aria-label', saved ? 'Saved. Remove from saved items' : 'Save this item');
      }

      button.addEventListener('click', function () {
        var k = key();
        if (!k || !window.WISH) return;
        window.WISH.toggle(k[0], k[1], k[2]);
        reflect();
      });

      // Repaint when the selection changes, and when anything anywhere else
      // changes the store.
      root.addEventListener('click', function (event) {
        if (event.target.closest('[data-colour-value], [data-size-value]')) {
          window.setTimeout(reflect, 0);
        }
      });
      document.addEventListener('wishlist:change', reflect);

      reflect();
    })();

    paint();
  }

  function initAll() {
    document.querySelectorAll('.core-pdp').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('.core-pdp').forEach(init);
  });
})();
